import {
  MutableRefObject,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDebouncedCallback } from "use-debounce";
import { Doc, useDoc } from "../lib/useDoc";
import { useContextMenu } from "react-contexify";
import { useGraphStore } from "../lib/useGraphStore";
import { Box } from "../slang";
import { GRAPH_CONTEXT_MENU_ID } from "./GraphContextMenu";
import throttle from "lodash.throttle";
import { useMutation } from "react-query";
import { renderDiagram } from "../lib/queries";
import svgPanZoom from "svg-pan-zoom";
import { Trans } from "@lingui/macro";
import { FFTheme } from "../lib/FFTheme";
import { useBackground } from "../lib/toTheme";
import equal from "fast-deep-equal";
import Loading from "./Loading";
import { useFullscreen } from "../lib/hooks";

declare global {
  interface Window {
    __panZoom?: SvgPanZoom.Instance;
  }
}

const KrokiGraph = memo(function KrokiGraph({
  shouldResize,
}: {
  shouldResize: number;
}) {
  const [initResizeNumber] = useState(shouldResize);

  // Sometimes we want to do things only on the first render
  const isFirstRender = useRef(true);
  const bg = useBackground();

  const graphRef = useRef<HTMLDivElement>();
  const panZoom = useRef<undefined | SvgPanZoom.Instance>();
  const [showRenderError, setShowRenderError] = useState<boolean>(false);
  const [errorPlaceholder, setErrorPlaceholder] = useState<string>(
    "/images/diagram_waiting.svg"
  );
  const [errorPlaceholderText, setErrorPlaceholderText] = useState<string>("");
  const [isLoading, setLoading] = useState<boolean>(true);

  const handleResize = useCallback(() => {
    if (!panZoom.current) {
      return;
    }
    panZoom.current.resize();
    panZoom.current.fit();
    panZoom.current.center();
  }, []);

  const debouncedResize = useDebouncedCallback(handleResize, 250);

  const isFullscreen = useFullscreen();

  useEffect(() => {
    window.addEventListener("resize", debouncedResize.callback);
    return () => window.removeEventListener("resize", debouncedResize.callback);
  }, [debouncedResize]);

  // Initialize Graph and Subscribe to doc for updates
  useEffect(() => {
    // const destroy = initializeGraph({ panZoom });
    const update = getGraphUpdater({
      isFirstRender,
    });
    isFirstRender.current = true;
    const unsubscribe = useDoc.subscribe((doc) => doc, update, {
      fireImmediately: true,
      equalityFn: equal,
    });
    return () => {
      unsubscribe();
      // if (destroy) destroy();
    };
  }, []);

  /**
   * Returns a debounced function that only relies
   * on the document to update the graph
   */
  function getGraphUpdater({
    isFirstRender,
  }: {
    isFirstRender: MutableRefObject<boolean>;
  }) {
    return throttle((_doc?: Doc) => {
      const doc = _doc ?? useDoc.getState();
      if (doc.text && doc.text.trim().length > 0) {
        setShowRenderError(false);
        setLoading(true);
        fetchDiagram.mutate({
          diagramText: doc.text,
          diagramType: doc.meta.diagramTool,
          diagramOptions: doc.meta.themeEditor as FFTheme,
        });
      } else {
        setErrorPlaceholder("/images/diagram_waiting.svg");
        setErrorPlaceholderText(
          "Insert your diagram content and the diagram will be show here!"
        );
        setShowRenderError(true);
      }
    }, 100);
  }

  const fetchDiagram = useMutation(
    "fetchDiagram",
    async (args: any) => {
      const { diagramType, diagramText, diagramOptions } = args;
      const result = await renderDiagram(
        diagramText,
        diagramType,
        "svg",
        diagramOptions
      );
      return result;
    },
    {
      onError: () => {
        setLoading(false);
        setShowRenderError(true);
      },
      onSuccess: (result) => {
        setLoading(false);
        if (result.startsWith("<svg") || result.startsWith("<?xml")) {
          setShowRenderError(false);
          if (graphRef.current) {
            graphRef.current.innerHTML = result;
            graphRef.current.getElementsByTagName("svg")[0].id = "container";
            const graphDiv = document.getElementById("container");
            if (graphDiv) {
              if (isFullscreen) {
                graphDiv.setAttribute("height", "100vh");
                graphDiv.setAttribute("width", "100vw");
                graphDiv.style.maxWidth = "100%";
              } else {
                graphDiv.setAttribute("height", "80vh");
                graphDiv.setAttribute("width", "100vw");
                graphDiv.style.maxWidth = "100%";
              }

              initializeGraph(panZoom);
            }
          }
        } else {
          setErrorPlaceholder("/images/syntax_error.svg");
          setErrorPlaceholderText(
            "Could not render diagram due to incorrect syntax."
          );
          setShowRenderError(true);
          if (graphRef.current) {
            graphRef.current.innerHTML = "<div/>";
          }
        }
      },
    }
  );

  const { show } = useContextMenu({ id: GRAPH_CONTEXT_MENU_ID });

  useEffect(() => {
    if (initResizeNumber !== shouldResize) handleResize();
  }, [handleResize, initResizeNumber, shouldResize]);

  // On unmount reset graph store
  useEffect(() => {
    return () => {
      useGraphStore.setState({
        autoFit: true,
        zoom: undefined,
        pan: undefined,
      });
    };
  }, []);

  return (
    <Box h="100%" style={{ background: bg }} onContextMenu={show}>
      {isLoading && (
        <div className="loading-layer">
          <Loading />
        </div>
      )}

      {showRenderError && (
        <div className="error-layer">
          <RenderError text={errorPlaceholderText} image={errorPlaceholder} />
        </div>
      )}
      <Box id="graph-div" overflow="hidden" ref={graphRef} />
      {/* <GraphContextMenu /> */}
    </Box>
  );
});

export default KrokiGraph;

function initializeGraph(
  panZoom: React.MutableRefObject<SvgPanZoom.Instance | undefined>
) {
  const graphDiv = document.getElementById("container");
  if (!graphDiv) {
    return;
  }
  panZoom.current = svgPanZoom("#container", {
    fit: true,
    maxZoom: 2,
    minZoom: 0.5,
    zoomScaleSensitivity: 0.2,
    panEnabled: true,
    controlIconsEnabled: false,
  });

  window.__panZoom = panZoom.current;

  return () => {
    panZoom.current?.destroy();
    delete window.__panZoom;
  };
}

function RenderError({ image, text }: { image: string; text: string }) {
  return (
    <div className="grid justify-content-center justify-items-center content-center">
      <img src={image} alt="an img" className="mx-auto" />
      <p className="content-center justify-content-center font-medium">
        <Trans>{text}</Trans>
      </p>
    </div>
  );
}
